import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Share2, ChevronLeft, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { AdminLayout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { LOGO_URL } from "@/components/Logo";

export default function AdminWeeklyReport() {
  const { user } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [orgId, setOrgId] = useState("");
  const [offset, setOffset] = useState(0); // 0 = this week
  const [report, setReport] = useState(null);

  useEffect(() => {
    api.get("/organizations").then(r => {
      setOrgs(r.data);
      if (user?.role === "local_admin" && user?.organization_id) setOrgId(user.organization_id);
      else if (r.data.length && !orgId) setOrgId(r.data[0].id);
    });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!orgId) return;
    api.get(`/reports/weekly/${orgId}`, { params: { offset } }).then(r => setReport(r.data));
  }, [orgId, offset]);

  const shareText = () => {
    if (!report) return "";
    const days = new Date(report.week_end).toLocaleDateString("sv-SE", { day: "numeric", month: "short" });
    const start = new Date(report.week_start).toLocaleDateString("sv-SE", { day: "numeric", month: "short" });
    const arrow = report.delta > 0 ? "↑" : report.delta < 0 ? "↓" : "→";
    const topList = report.top_locations.slice(0, 3);
    const topSection = topList.length > 0
      ? "\n\nToppplatser:\n" + topList.map((l, i) => `${i + 1}. ${l.name} — ${l.conversations} samtal`).join("\n")
      : "";
    return `📊 Veckorapport · ${report.organization.name}\n${start}–${days}\n\n${report.total_conversations.toLocaleString("sv-SE")} samtal ${arrow}${report.delta_pct !== null ? ` (${report.delta_pct > 0 ? "+" : ""}${report.delta_pct}% mot förra veckan)` : ""}\n${report.activities} aktiviteter · ${report.active_members} aktiva medlemmar${topSection}\n\nSocialdemokraterna Skåne · Snacket`;
  };

  const copySummary = async () => {
    try { await navigator.clipboard.writeText(shareText()); toast.success("Sammanfattning kopierad!"); }
    catch { toast.error("Kunde inte kopiera"); }
  };

  const nativeShare = async () => {
    if (!navigator.share) { copySummary(); return; }
    try { await navigator.share({ title: "Veckorapport", text: shareText() }); }
    catch (err) {
      // User cancelled the share sheet or share failed silently — no user-facing action needed.
      if (err?.name !== "AbortError") console.warn("[weekly-report] native share failed", err);
    }
  };

  const canPickOrg = user?.role === "central_admin";
  const weekLabel = offset === 0 ? "Denna vecka" : offset === 1 ? "Förra veckan" : `${offset} veckor sedan`;
  const maxDay = report ? Math.max(1, ...report.days.map(d => d.conversations)) : 1;

  return (
    <AdminLayout title="Veckorapport" subtitle="Delningsbar sammanfattning per Arbetarekommun" actions={
      <div className="flex gap-2">
        <Button data-testid="wr-copy" variant="outline" onClick={copySummary}><Copy size={14} className="mr-1"/>Kopiera text</Button>
        <Button data-testid="wr-share" onClick={nativeShare} className="bg-[#E1251B] hover:bg-[#C81D14] rounded-full"><Share2 size={14} className="mr-1"/>Dela</Button>
      </div>
    }>
      <div className="flex flex-wrap gap-3 items-center mb-6">
        {canPickOrg && (
          <div className="w-72">
            <Select value={orgId} onValueChange={setOrgId}>
              <SelectTrigger data-testid="wr-org"><SelectValue/></SelectTrigger>
              <SelectContent>{orgs.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-full p-1">
          <Button data-testid="wr-prev" size="sm" variant="ghost" className="rounded-full" onClick={() => setOffset(o => o + 1)}><ChevronLeft size={14}/></Button>
          <div className="px-3 text-sm font-semibold text-slate-700 min-w-[140px] text-center">{weekLabel}</div>
          <Button data-testid="wr-next" size="sm" variant="ghost" className="rounded-full" disabled={offset === 0} onClick={() => setOffset(o => Math.max(0, o - 1))}><ChevronRight size={14}/></Button>
        </div>
      </div>

      {!report && <div className="text-slate-400">Laddar…</div>}
      {report && (
        <div className="max-w-3xl">
          {/* SHAREABLE CARD */}
          <div className="rounded-3xl bg-gradient-to-br from-slate-900 to-slate-800 text-white p-10 shadow-lg relative overflow-hidden" data-testid="wr-card">
            <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-[#E1251B] opacity-20 blur-3xl"/>
            <div className="relative">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase tracking-widest font-semibold opacity-60">Veckorapport</div>
                  <h2 className="font-heading text-4xl font-extrabold mt-1" data-testid="wr-org-name">{report.organization.name}</h2>
                  <div className="text-sm opacity-70 mt-1">{new Date(report.week_start).toLocaleDateString("sv-SE", { day: "numeric", month: "short" })} – {new Date(report.week_end).toLocaleDateString("sv-SE", { day: "numeric", month: "short", year: "numeric" })}</div>
                </div>
                <div className="text-right flex items-start gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest opacity-60 font-semibold text-right">Socialdemokraterna</div>
                    <div className="font-heading text-lg font-extrabold text-right">Skåne</div>
                  </div>
                  <img src={LOGO_URL} alt="Socialdemokraterna Skåne" style={{ height: 56, filter: "brightness(0) invert(1)" }}/>
                </div>
              </div>

              <div className="mt-10">
                <div className="text-xs uppercase tracking-widest opacity-60 font-semibold">Samtal</div>
                <div className="flex items-baseline gap-3 mt-1">
                  <div className="font-heading text-7xl font-extrabold number-tabular" data-testid="wr-total">{report.total_conversations.toLocaleString("sv-SE")}</div>
                  {report.delta_pct !== null && (
                    <div className={`text-sm font-bold px-3 py-1 rounded-full ${report.delta >= 0 ? "bg-emerald-500/20 text-emerald-300" : "bg-rose-500/20 text-rose-300"}`}>
                      {report.delta > 0 ? "▲" : report.delta < 0 ? "▼" : "→"} {report.delta_pct > 0 ? "+" : ""}{report.delta_pct}%
                    </div>
                  )}
                </div>
                <div className="text-sm opacity-60 mt-1">mot förra veckans {report.prev_total.toLocaleString("sv-SE")} samtal</div>
              </div>

              <div className="grid grid-cols-2 gap-4 mt-8 pt-8 border-t border-white/10">
                <div>
                  <div className="text-xs uppercase tracking-widest opacity-60 font-semibold">Aktiviteter</div>
                  <div className="font-heading text-3xl font-extrabold mt-1 number-tabular">{report.activities}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest opacity-60 font-semibold">Aktiva medlemmar</div>
                  <div className="font-heading text-3xl font-extrabold mt-1 number-tabular">{report.active_members}</div>
                </div>
              </div>

              {/* Daily bars */}
              <div className="mt-8 pt-8 border-t border-white/10">
                <div className="text-xs uppercase tracking-widest opacity-60 font-semibold mb-3">Fördelning per dag</div>
                <div className="flex items-end justify-between gap-2 h-24">
                  {report.days.map((d, i) => (
                    <div key={d.date || `day-${i}`} className="flex-1 flex flex-col items-center gap-1.5">
                      <div className="w-full bg-white/10 rounded-t-md relative flex flex-col justify-end overflow-hidden" style={{ height: "80px" }}>
                        <div className="bg-[#E1251B] rounded-t-md w-full transition-all duration-500" style={{ height: `${(d.conversations / maxDay) * 100}%` }}/>
                      </div>
                      <div className="text-[10px] opacity-60 font-semibold">{["M","T","O","T","F","L","S"][i]}</div>
                      <div className="text-[10px] font-bold number-tabular">{d.conversations}</div>
                    </div>
                  ))}
                </div>
              </div>

              {report.top_locations.length > 0 && (
                <div className="mt-8 pt-8 border-t border-white/10">
                  <div className="text-xs uppercase tracking-widest opacity-60 font-semibold mb-3">Toppplatser</div>
                  <div className="space-y-2">
                    {report.top_locations.map((l, i) => (
                      <div key={l.id || l.name} className="flex justify-between items-center py-1" data-testid={`wr-top-${l.name}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-xs font-bold">{i + 1}</div>
                          <span className="font-semibold">{l.name}</span>
                        </div>
                        <div className="font-heading font-bold number-tabular">{l.conversations}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <p className="text-xs text-slate-500 mt-4 text-center">Sammanfattningen är avidentifierad — inga individuella medlemsuppgifter delas.</p>
        </div>
      )}
    </AdminLayout>
  );
}
